import { supabase } from '../supabase/client';

// ===== USER MANAGEMENT SERVICE =====
export class UserService {
  /**
   * Create or update user profile
   */
  static async upsertUser(userData: {
    firebase_uid: string;
    email: string;
    display_name?: string;
    photo_url?: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    job_title?: string;
    bio?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('users')
        .upsert(userData, {
          onConflict: 'firebase_uid',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error upserting user:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user by Firebase UID
   */
  static async getUserByFirebaseUid(firebaseUid: string) {
    try {
      console.log('🔍 Fetching user from database:', firebaseUid);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('firebase_uid', firebaseUid)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid PGRST116 error

      if (error) {
        console.error('❌ Supabase error getting user:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return { data: null, error };
      }
      
      if (data) {
        console.log('✅ User found in database');
      } else {
        console.log('ℹ️ User not found in database (this is normal for new users)');
      }
      
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Exception getting user:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user profile
   */
  static async updateUser(firebaseUid: string, updates: {
    display_name?: string;
    photo_url?: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    job_title?: string;
    bio?: string;
    notification_preferences?: Record<string, any>;
    ui_preferences?: Record<string, any>;
    privacy_settings?: Record<string, any>;
  }) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('firebase_uid', firebaseUid)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating user:', error);
      return { data: null, error };
    }
  }

  /**
   * Soft delete user (set deleted_at timestamp)
   */
  static async softDeleteUser(firebaseUid: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('firebase_uid', firebaseUid)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error soft deleting user:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user activity tracking
   */
  static async updateUserActivity(firebaseUid: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          last_activity_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('firebase_uid', firebaseUid)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating user activity:', error);
      return { data: null, error };
    }
  }
}

// ===== USER ANALYTICS SERVICE =====
export class AnalyticsService {
  /**
   * Track user event
   */
  static async trackEvent(eventData: {
    user_id?: string;
    firebase_uid: string;
    event_type: string;
    event_category?: string;
    event_action?: string;
    event_label?: string;
    analysis_type?: string;
    processing_time_seconds?: number;
    file_size_mb?: number;
    api_endpoint?: string;
    http_method?: string;
    response_status?: number;
    response_time_ms?: number;
    credits_used?: number;
    cost_incurred?: number;
    event_metadata?: Record<string, any>;
    video_preview?: string;
    task_id?: string;
    video_id?: string;
    video_name?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_analytics')
        .insert({
          ...eventData,
          user_agent: typeof window !== 'undefined' ? navigator.userAgent : null,
          event_timestamp: new Date().toISOString(),
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error tracking event:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user analytics
   */
  static async getUserAnalytics(firebaseUid: string, options: {
    limit?: number;
    eventType?: string;
    startDate?: string;
    endDate?: string;
  } = {}) {
    try {
      let query = supabase
        .from('user_analytics')
        .select('*')
        .eq('firebase_uid', firebaseUid)
        .order('created_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.eventType) {
        query = query.eq('event_type', options.eventType);
      }

      if (options.startDate) {
        query = query.gte('created_at', options.startDate);
      }

      if (options.endDate) {
        query = query.lte('created_at', options.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting user analytics:', error);
      return { data: null, error };
    }
  }

  /**
   * Update analytics event
   */
  static async updateEvent(eventId: string, updates: {
    processing_time_seconds?: number;
    response_status?: number;
    response_time_ms?: number;
    credits_used?: number;
    cost_incurred?: number;
    event_metadata?: Record<string, any>;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_analytics')
        .update(updates)
        .eq('id', eventId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating analytics event:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete analytics events (for GDPR compliance)
   */
  static async deleteUserAnalytics(firebaseUid: string, olderThanDays?: number) {
    try {
      let query = supabase
        .from('user_analytics')
        .delete()
        .eq('firebase_uid', firebaseUid);

      if (olderThanDays) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
        query = query.lt('created_at', cutoffDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting analytics:', error);
      return { data: null, error };
    }
  }
}

// ===== USER PURCHASES SERVICE =====
export class PurchaseService {
  /**
   * Create purchase record
   */
  static async createPurchase(purchaseData: {
    user_id?: string;
    firebase_uid: string;
    product_id: string;
    product_name: string;
    product_type: string;
    quantity?: number;
    unit_price: number;
    total_amount: number;
    currency?: string;
    discount_amount?: number;
    payment_provider?: string;
    payment_method?: string;
    transaction_id?: string;
    payment_status?: string;
    subscription_period_start?: string;
    subscription_period_end?: string;
    is_renewal?: boolean;
    credits_purchased?: number;
    credits_remaining?: number;
    purchase_metadata?: Record<string, any>;
    notes?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_purchases')
        .insert({
          ...purchaseData,
          purchased_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error creating purchase:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user purchases
   */
  static async getUserPurchases(firebaseUid: string, options: {
    limit?: number;
    status?: string;
    productType?: string;
  } = {}) {
    try {
      let query = supabase
        .from('user_purchases')
        .select('*')
        .eq('firebase_uid', firebaseUid)
        .order('purchased_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.status) {
        query = query.eq('payment_status', options.status);
      }

      if (options.productType) {
        query = query.eq('product_type', options.productType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting purchases:', error);
      return { data: null, error };
    }
  }

  /**
   * Update purchase status
   */
  static async updatePurchase(purchaseId: string, updates: {
    payment_status?: string;
    activated_at?: string;
    expires_at?: string;
    refunded_at?: string;
    credits_remaining?: number;
    purchase_metadata?: Record<string, any>;
    notes?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_purchases')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', purchaseId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating purchase:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete purchase record
   */
  static async deletePurchase(purchaseId: string) {
    try {
      const { data, error } = await supabase
        .from('user_purchases')
        .delete()
        .eq('id', purchaseId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting purchase:', error);
      return { data: null, error };
    }
  }
}

// ===== USER PROFILES SERVICE =====
export class ProfileService {
  /**
   * Create user profile
   */
  static async createProfile(profileData: {
    user_id: string;
    email?: string;
    display_name?: string;
    company?: string;
    role?: string;
    bio?: string;
    location?: string;
    website?: string;
    avatar_url?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          ...profileData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error creating profile:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user profile
   */
  static async getProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting profile:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user profile
   */
  static async updateProfile(userId: string, updates: {
    email?: string;
    display_name?: string;
    company?: string;
    role?: string;
    bio?: string;
    location?: string;
    website?: string;
    avatar_url?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating profile:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete user profile
   */
  static async deleteProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting profile:', error);
      return { data: null, error };
    }
  }
}

// ===== SUBSCRIPTION PLANS SERVICE =====
export class SubscriptionPlanService {
  /**
   * Get all active subscription plans
   */
  static async getActivePlans() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting subscription plans:', error);
      return { data: null, error };
    }
  }

  /**
   * Create subscription plan (admin only)
   */
  static async createPlan(planData: {
    id: string;
    name: string;
    description?: string;
    credits_per_month?: number;
    price_monthly: number;
    price_yearly: number;
    stripe_price_id_monthly?: string;
    stripe_price_id_yearly?: string;
    stripe_product_id?: string;
    features: Record<string, any>;
    limitations: Record<string, any>;
    is_popular?: boolean;
    is_active?: boolean;
  }) {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .insert({
          ...planData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error creating subscription plan:', error);
      return { data: null, error };
    }
  }

  /**
   * Update subscription plan (admin only)
   */
  static async updatePlan(planId: string, updates: {
    name?: string;
    description?: string;
    credits_per_month?: number;
    price_monthly?: number;
    price_yearly?: number;
    stripe_price_id_monthly?: string;
    stripe_price_id_yearly?: string;
    stripe_product_id?: string;
    features?: Record<string, any>;
    limitations?: Record<string, any>;
    is_popular?: boolean;
    is_active?: boolean;
  }) {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', planId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating subscription plan:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete subscription plan (admin only)
   */
  static async deletePlan(planId: string) {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', planId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting subscription plan:', error);
      return { data: null, error };
    }
  }
}

// ===== CONTACT SALES SERVICE =====
export class ContactSalesService {
  /**
   * Create contact sales request
   */
  static async createContactRequest(contactData: {
    user_id?: string;
    user_name: string;
    company_name: string;
    email: string;
    message: string;
    plan_interested: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('contact_sales')
        .insert({
          ...contactData,
          submitted_at: new Date().toISOString(),
          status: 'new',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error creating contact request:', error);
      return { data: null, error };
    }
  }

  /**
   * Get contact sales requests (admin only)
   */
  static async getContactRequests(options: {
    limit?: number;
    status?: string;
  } = {}) {
    try {
      let query = supabase
        .from('contact_sales')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.status) {
        query = query.eq('status', options.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting contact requests:', error);
      return { data: null, error };
    }
  }

  /**
   * Update contact sales request status (admin only)
   */
  static async updateContactRequest(requestId: number, updates: {
    status?: string;
    sales_notes?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('contact_sales')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating contact request:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete contact sales request (admin only)
   */
  static async deleteContactRequest(requestId: number) {
    try {
      const { data, error } = await supabase
        .from('contact_sales')
        .delete()
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting contact request:', error);
      return { data: null, error };
    }
  }
}

// ===== CREDIT TRANSACTIONS SERVICE =====
export class CreditTransactionService {
  /**
   * Update credit transaction (for corrections)
   */
  static async updateTransaction(transactionId: string, updates: {
    description?: string;
    metadata?: Record<string, any>;
  }) {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .update(updates)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error updating credit transaction:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete credit transaction (for corrections/refunds)
   */
  static async deleteTransaction(transactionId: string) {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .delete()
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting credit transaction:', error);
      return { data: null, error };
    }
  }
}

// ===== BILLING EVENTS SERVICE =====
export class BillingEventService {
  /**
   * Delete processed billing events (cleanup)
   */
  static async deleteProcessedEvents(olderThanDays: number = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const { data, error } = await supabase
        .from('billing_events')
        .delete()
        .eq('processed', true)
        .lt('processed_at', cutoffDate.toISOString());

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error deleting billing events:', error);
      return { data: null, error };
    }
  }

  /**
   * Get unprocessed billing events
   */
  static async getUnprocessedEvents(limit: number = 100) {
    try {
      const { data, error } = await supabase
        .from('billing_events')
        .select('*')
        .eq('processed', false)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('❌ Error getting unprocessed events:', error);
      return { data: null, error };
    }
  }
}

// Services are already exported individually above
